import { z } from 'zod';
import { TagStatuses } from '@shared/constants/statuses.const';
import { EquipmentTypes } from '@shared/interfaces/Equipment/EquipmentTypes';
import { tradeSchema } from '@shared/interfaces/ITrade';
import { baseManagerSchema } from '@shared/interfaces/IBaseManager';
import { baseStaticAnchorSchema } from '@shared/interfaces/IBaseStaticAnchor';
import { dateField } from '@shared/schemas';
import { calendarDateField } from '@shared/contracts/schemas';

// === Raw Tag Schemas (tag ingestion from hardware) ===

const rawAnchorSchema = z.object({
	uuid: z.string(),
	batteryLevel: z.string(),
	RSSI: z.string(),
});

export const rawTagSchema = z.object({
	uuid: z.string(),
	batteryLevel: z.string().optional(),
	isSleeping: z.boolean().optional(),
	closestAnchor: rawAnchorSchema,
	closestEquipment: z.array(rawAnchorSchema),
	runnerUpAnchors: z.array(rawAnchorSchema),
	loraRSSI: z.number().optional(),
	gateway_id: z.string().optional(),
});

// === Static Tag Schemas ===

const tagStatusSchema = z.enum(TagStatuses);

const tagCertificateApiInputSchema = z.object({
	name: z.string(),
	expirationDate: calendarDateField,
	creationDate: dateField,
});

/**
 * `originProjectId` — the static tag's project field — is deliberately absent
 * from both create bodies. It comes from `/projects/:projectId` and the
 * repository stamps it. Note it was never a project key the central guard could
 * read: `collectRequestProjectIds` looks for `projectId`/`projectIds`, so a body
 * naming a foreign `originProjectId` reached the handler unauthorized.
 */
export const createStaticTagSchema = z.object({
	tagId: z.string(),
	tagNick: z.string().optional(),
	_id: z.string().optional(),
	organizationId: z.string(),
	tagStatus: tagStatusSchema.optional(),
});

export const baseStaticTagApiInputSchema = z.object({
	tagId: z.string(),
	tagNick: z.string(),
	_id: z.string().optional(),
	lastUpdateDate: dateField,
	organizationId: z.string(),
	tagStatus: tagStatusSchema,
	certificates: z.array(tagCertificateApiInputSchema).optional(),
});

/**
 * updateStaticTag body — Partial of IStaticTagApiInput (union of worker/manager/utility).
 * All fields optional. Uses looseObject to allow any variant's fields without index signature.
 *
 * The looseness STAYS: it is carrying the polymorphic worker/manager/utility
 * fields, plus whatever legacy clients send that nobody has enumerated, and
 * declaring the shape here would 400 them.
 *
 * `originProjectId` — this collection's OWNING project field, and the only
 * dangerous key in here — is dropped where each `$set` is built (the tag
 * repository, and the merged-tag write next to it), silently, so an old payload
 * keeps working minus the field. `.omit()` would not help: on a loose object an
 * omitted key survives as an unrecognized one. Note the central guard reads
 * `projectId`/`projectIds` only, so it never saw this field name at all.
 */
export const updateStaticTagBodySchema = z.looseObject({
	tagId: z.string().optional(),
	tagNick: z.string().optional(),
	tagStatus: tagStatusSchema.optional(),
	certificates: z.array(tagCertificateApiInputSchema).optional(),
});

// === Trade / Manager Schemas (reused from shared) ===

export { tradeSchema, baseManagerSchema };

// === Anchor Schemas (re-export from shared interface) ===

export { baseStaticAnchorSchema };

// === Equipment Schemas ===

/** `projectId` comes from `/projects/:projectId`; the repository stamps it. */
export const staticEquipmentInputSchema = z.object({
	company: z.string(),
	serialNumber: z.string(),
	name: z.string(),
	type: z.enum(EquipmentTypes),
	typeNick: z.string(),
	equipmentId: z.string(),
	lastSeenDate: dateField,
});

/**
 * `Omit<Partial<IStaticEquipment>, '_id' | 'projectId'>`, spelled out.
 *
 * This was `z.record(z.string(), z.unknown())` — literally any key — and the
 * handler forwarded it verbatim as the `$set`. `PUT { "projectId": "other" }`
 * moved the row into another project: the authorization filter decided which row
 * was MATCHED, never what was WRITTEN. `equipmentId` is excluded too; it is the
 * addressing key and re-keying a row through an update is the same defect one
 * step removed.
 */
export const updateEquipmentBodySchema = staticEquipmentInputSchema.omit({ equipmentId: true }).partial();
